import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';

import {
    Accessory,
    IOptionData,
    Option,
    OptionSalable,
    Plan,
    PRICE_TYPES,
    Product,
    ProductFactory,
    QuoteModel,
    Sensation,
    Sowo
} from '@bytel/bytel-sales';

import { ConfigurationService } from '@services/configuration.service';
import { FaiService } from '@services/fai.services';
import { HandleErrorService } from '@services/handle-error.service';
import { MainCartService } from '@services/main-cart.service';
import { OptionsService } from '@services/options.service';

import { AddProductConfig, BasicObject, RemoveProductConfig } from '@interfaces/checkout.interface';

import { Observable, Subject, Subscription, catchError, of, takeUntil } from 'rxjs';

import { QuoteHelper } from '../../../helper/quote-helper';
import { QtyCountPipe } from '../../../shared/pipes/qtyCount.pipe';
import { CrosssellItemComponent } from './crosssell-item/crosssell-item.component';

@Component({
    selector: 'checkout-basic-crosssells',
    templateUrl: './basic-crosssells.component.html',
    styleUrl: 'basic-crosssells.component.scss',
    standalone: true,
    imports: [CrosssellItemComponent],
})
export class BasicCrosssellsComponent implements OnInit, OnDestroy {
    public static CROSSSELL_TYPES: BasicObject = {
        OPTION: 'option',
        ACCESSORY: 'accessory',
    };

    public crossSellItemsCarouselOptions = {
        groupCells: 1,
        cellAlign: 'left',
        wrapAround: false,
        pageDots: true,
        prevNextButtons: false,
    };

    public cartQuoteSubscription: Subscription;
    public config: BasicObject;
    public products: Product[] = [];
    public showProducts: boolean = true;
    public totalProductsQty: number = 0;
    public canAddProduct: boolean = true;
    @Input() public type: string;
    @Input() public canAdd: boolean = true;
    @Input() public quoteIndex: number = 0;

    private _ngUnsubscribe = new Subject();
    private cartService: MainCartService = inject(MainCartService);
    private configurationService: ConfigurationService = inject(ConfigurationService);
    private handleErrorService: HandleErrorService = inject(HandleErrorService);
    private optionsService: OptionsService = inject(OptionsService);
    private faiService: FaiService = inject(FaiService);

    public addProduct(productConfig: AddProductConfig): void {
        const { product, qty } = productConfig;
        this.cartService
            .updateProductQty(product, qty)
            .pipe(
                takeUntil(this._ngUnsubscribe),
                catchError((error) => {
                    this.handleErrorService.handleError(error);
                    return of();
                }),
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        this.cartQuoteSubscription.unsubscribe();
        this._ngUnsubscribe.next(null);
        this._ngUnsubscribe.complete();
    }

    public ngOnInit(): void {
        this._initWallurl();

        this.cartQuoteSubscription = this.cartService.cartQuoteSubscription.subscribe(() => {
            this._updateProducts();
        });
    }

    public removeProduct($event: RemoveProductConfig): void {
        const { product, removeAll } = $event;
        let obs: Observable<any>;
        if (ProductFactory.Is(product, OptionSalable) && this.faiService.isFmsToPayTodayByCategory((product.data as IOptionData).option_category)) {
            this.faiService.updateFmsPriceType(PRICE_TYPES.Reported);
        }
        if (removeAll) {
            obs = this.cartService.removeProductsByGencode(product.gencode, this.quoteIndex);
        } else {
            obs = this.cartService.removeProduct(product, this.quoteIndex);
        }
        obs.pipe(takeUntil(this._ngUnsubscribe)).subscribe();
    }

    private _updateProducts(): void {
        const quote: QuoteModel = this.cartService.getQuote(this.quoteIndex);
        switch (this.type) {
            case BasicCrosssellsComponent.CROSSSELL_TYPES.OPTION:
                let options: Option[] = quote.getProductsByType<Option>('Option');
                options = this.optionsService.excludeParentalOptions(options);
                this.products = QuoteHelper.MergeProductByGencode(options);
                break;

            case BasicCrosssellsComponent.CROSSSELL_TYPES.ACCESSORY:
                this.products = QuoteHelper.MergeProductByGencode(quote.getProductsByType<Accessory>('Accessory'));
                break;
            default:
                console.warn('unknown type of product');
        }
        this._initWallurl();
        const qtyPipe = new QtyCountPipe(this.cartService);
        this.totalProductsQty = qtyPipe.transform(this.products);
        this._canAddProduct();
    }

    private _initWallurl(): void {
        const hasSowo: boolean = !!this.cartService.getQuote().getProductByType(Sowo);
        const wallUrls: BasicObject = this.configurationService.getData()?.global.wall;
        this.config = {
            [BasicCrosssellsComponent.CROSSSELL_TYPES.OPTION]: {
                title: 'Mes options',
                homeTitle: 'Ajouter une option',
                url: wallUrls.cross_sell,
            },
            [BasicCrosssellsComponent.CROSSSELL_TYPES.ACCESSORY]: {
                title: 'Mes accessoires',
                homeTitle: 'Ajouter un accessoire',
                url: hasSowo ? wallUrls.cross_sell : wallUrls[BasicCrosssellsComponent.CROSSSELL_TYPES.ACCESSORY],
            },
        };
    }

    /**
     * Disable add possibility if FAM && no products added before
     */
    private _canAddProduct(): void {
        const principalProduct = this.cartService.getQuote().getPrincipalProduct(Plan);
        if (
            (ProductFactory.Is(principalProduct, Sensation) &&
                !this.cartService.crossSellActiveForFAMOnCheckout() &&
                this.products.length === 0) ||
            this.totalProductsQty === 5
        ) {
            this.canAddProduct = false;
        } else {
            this.canAddProduct = true;
        }
    }
}
